import type { HelpArea, HelpLine, HelpPlatform, HelpTopic } from './helpTopics';

/**
 * Pure helpers for the Help guide: which lines a platform sees, the bold names inside a line, and
 * the search box. No React here, so jest tests it directly.
 */

/** A line's text for this platform, or null when it is only for the other one. */
export function lineFor(line: HelpLine, platform: HelpPlatform): string | null {
  if (typeof line === 'string') { return line; }
  return line.on === platform ? line.text : null;
}

/** A topic's steps and notes as this platform shows them. */
export function topicLines(topic: HelpTopic, platform: HelpPlatform): { steps: string[]; notes: string[] } {
  const pick = (lines: HelpLine[] = []) => lines.map(l => lineFor(l, platform)).filter((t): t is string => !!t);
  return { steps: pick(topic.steps), notes: pick(topic.notes) };
}

/** "Tap **Save**." -> [{ text: 'Tap ' }, { text: 'Save', bold: true }, { text: '.' }]. */
export function richParts(text: string): { text: string; bold: boolean }[] {
  return text.split('**')
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter(p => p.text.length > 0);
}

/** The text without the bold markers. */
export const plainText = (text: string) => text.replace(/\*\*/g, '');

/** Lower case, no bold markers or punctuation, single spaces: what search compares. */
export function normalise(text: string): string {
  return plainText(text)
    .toLowerCase()
    .replace(/[‘’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** "fees" also finds "fee"; "cancelled" finds "cancel". Simple endings only. */
function wordForms(word: string): string[] {
  const forms = [word];
  if (word.length > 4 && word.endsWith('ies')) { forms.push(`${word.slice(0, -3)}y`); }
  if (word.length > 3 && word.endsWith('s')) { forms.push(word.slice(0, -1)); }
  if (word.length > 5 && word.endsWith('led')) { forms.push(word.slice(0, -3)); }
  if (word.length > 4 && word.endsWith('ed')) { forms.push(word.slice(0, -2)); }
  if (word.length > 5 && word.endsWith('ing')) { forms.push(word.slice(0, -3)); }
  return forms;
}

const has = (haystack: string, word: string) => wordForms(word).some(f => haystack.includes(f));

/**
 * The topics matching every word of the query, best first: a word in the title counts most, then
 * the feature's name and the topic's keywords, then its steps and notes. An empty query returns
 * every topic in order. Only the lines for this platform are searched.
 */
export function searchHelpTopics(topics: HelpTopic[], query: string, platform: HelpPlatform): HelpTopic[] {
  const words = normalise(query).split(' ').filter(Boolean);
  if (words.length === 0) { return topics; }

  const scored: { topic: HelpTopic; score: number; order: number }[] = [];
  topics.forEach((topic, order) => {
    const title = normalise(topic.title);
    const tags = normalise([topic.area, ...(topic.keywords ?? [])].join(' '));
    const { steps, notes } = topicLines(topic, platform);
    const body = normalise([...steps, ...notes].join(' '));
    let score = 0;
    for (const word of words) {
      if (has(title, word)) { score += 3; }
      else if (has(tags, word)) { score += 2; }
      else if (has(body, word)) { score += 1; }
      else { return; }
    }
    scored.push({ topic, score, order });
  });
  return scored.sort((a, b) => b.score - a.score || a.order - b.order).map(s => s.topic);
}

/** Topics under their feature, in the guide's order; features with no topics are left out. */
export function groupByArea(topics: HelpTopic[], areas: readonly HelpArea[]): { area: HelpArea; topics: HelpTopic[] }[] {
  return areas
    .map(area => ({ area, topics: topics.filter(t => t.area === area) }))
    .filter(g => g.topics.length > 0);
}
